import { useState } from 'react';
import { Alert, api, Badge, Button, Input, ListPage, Modal, Select, useFetch, useForm, usePagedList } from '@greenrinse/ui';
import { GeofenceEditor, GeofenceValue } from '../components/GeofenceEditor';
import type { Partner } from './PartnersPage';

export interface Site {
  id: string; partnerId: string; partnerName: string; countryId: string; countryCode: string; name: string; code: string | null; address: string; city: string | null;
  location: { lat: number; lng: number }; radiusMeters: number; polygon: { lat: number; lng: number }[] | null; timezone: string;
  gateCode: string | null; parkingLevel: string | null; contactName: string | null; contactPhone: string | null; accessNotes: string | null;
  workingHoursFrom: string | null; workingHoursTo: string | null; isActive: boolean; vehicleCount: number;
}

const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };

export function SiteForm({ site, partnerId, onSaved, onCancel }: { site: Site | null; partnerId?: string; onSaved: (s: Site) => void; onCancel: () => void }) {
  const partners = useFetch<{ items: Partner[] }>('/api/partners?pageSize=200');
  const form = useForm({
    partnerId: site?.partnerId ?? partnerId ?? '', name: site?.name ?? '', code: site?.code ?? '', address: site?.address ?? '', city: site?.city ?? '',
    timezone: site?.timezone ?? 'Asia/Dubai', gateCode: site?.gateCode ?? '', parkingLevel: site?.parkingLevel ?? '', contactName: site?.contactName ?? '',
    contactPhone: site?.contactPhone ?? '', accessNotes: site?.accessNotes ?? '', workingHoursFrom: site?.workingHoursFrom ?? '', workingHoursTo: site?.workingHoursTo ?? '',
    isActive: site?.isActive ?? true,
  });
  const [fence, setFence] = useState<GeofenceValue>({ location: site?.location ?? DEFAULT_CENTER, radiusMeters: site?.radiusMeters ?? 150, polygon: site?.polygon ?? null });

  const save = () => form.submit(async (v) => {
    const body = {
      ...v, partnerId: v.partnerId || partners.data?.items[0]?.id, code: v.code || null, city: v.city || null, gateCode: v.gateCode || null, parkingLevel: v.parkingLevel || null,
      contactName: v.contactName || null, contactPhone: v.contactPhone || null, accessNotes: v.accessNotes || null,
      workingHoursFrom: v.workingHoursFrom || null, workingHoursTo: v.workingHoursTo || null,
      location: fence.location, radiusMeters: fence.radiusMeters, polygon: fence.polygon && fence.polygon.length >= 3 ? fence.polygon : null,
    };
    const saved = site ? await api<Site>(`/api/sites/${site.id}`, { method: 'PUT', body }) : await api<Site>('/api/sites', { method: 'POST', body });
    onSaved(saved);
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-form-grid">
      {form.message && <div className="gr-span-2" style={{ marginBottom: 12 }}><Alert tone="error">{form.message}</Alert></div>}
      <Select label="Partner" {...form.bind('partnerId')} disabled={!!partnerId || !!site}
        options={(partners.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))} />
      <Input label="Site name" {...form.bind('name')} />
      <Input label="Site code" hint="Used in fleet imports (Site Code column)" {...form.bind('code')} />
      <Input label="Timezone (IANA)" {...form.bind('timezone')} />
      <div className="gr-span-2"><Input label="Address" {...form.bind('address')} /></div>
      <Input label="City" {...form.bind('city')} />
      <label className="gr-row" style={{ marginBottom: 16 }}><input type="checkbox" checked={form.values.isActive} onChange={(e) => form.set('isActive', e.target.checked)} /> Active</label>

      <div className="gr-span-2" style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Geofence</label>
        <GeofenceEditor value={fence} onChange={setFence} />
        {form.errors['polygon'] && <span className="gr-error-text">{form.errors['polygon'][0]}</span>}
      </div>

      <h3 className="gr-span-2">Access notes for washers</h3>
      <Input label="Gate code" {...form.bind('gateCode')} />
      <Input label="Parking level" {...form.bind('parkingLevel')} />
      <Input label="Contact name" {...form.bind('contactName')} />
      <Input label="Contact phone" {...form.bind('contactPhone')} />
      <Input label="Working hours from" type="time" {...form.bind('workingHoursFrom')} />
      <Input label="Working hours to" type="time" {...form.bind('workingHoursTo')} />
      <div className="gr-span-2 gr-field"><label>Notes</label><textarea className="gr-textarea" rows={3} value={form.values.accessNotes} onChange={(e) => form.set('accessNotes', e.target.value)} /></div>

      <div className="gr-span-2 gr-row" style={{ justifyContent: 'flex-end' }}>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={form.busy}>Save</Button>
      </div>
    </form>
  );
}

export default function SitesPage({ partnerId }: { partnerId?: string }) {
  const list = usePagedList<Site>('/api/sites', { partnerId, includeInactive: true });
  const [editing, setEditing] = useState<Site | 'new' | null>(null);

  return (
    <>
      <ListPage<Site>
        title={partnerId ? 'Sites' : 'Sites'} state={list} rowKey={(s) => s.id} onRowClick={(s) => setEditing(s)}
        actions={<Button onClick={() => setEditing('new')}>Add site</Button>}
        columns={[
          { key: 'name', header: 'Site', render: (s) => <><strong>{s.name}</strong>{s.code && <span className="gr-muted"> · {s.code}</span>}<div className="gr-muted" style={{ fontSize: 13 }}>{s.address}</div></> },
          ...(partnerId ? [] : [{ key: 'partner', header: 'Partner', render: (s: Site) => s.partnerName }]),
          { key: 'fence', header: 'Geofence', render: (s) => s.polygon ? <Badge tone="accent">Polygon · {s.polygon.length} pts</Badge> : <Badge>Radius {s.radiusMeters} m</Badge> },
          { key: 'tz', header: 'Timezone', render: (s) => s.timezone },
          { key: 'vehicles', header: 'Vehicles', render: (s) => s.vehicleCount, align: 'end' },
          { key: 'status', header: '', render: (s) => s.isActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge> },
        ]}
      />
      <Modal open={editing !== null} wide title={editing === 'new' ? 'New site' : 'Edit site'} onClose={() => setEditing(null)}>
        {editing !== null && (
          <SiteForm site={editing === 'new' ? null : editing} partnerId={partnerId} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />
        )}
      </Modal>
    </>
  );
}
